import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { memo, useContext, useEffect, useState } from "react";
import * as yup from "yup";

import { ErrorMessage, Formik } from "formik";
import appStyles from "../../../App.module.css";
import { formatAmount } from "../../../app-constants";
import { CorporateGroupTravelContext } from "../../CorporateGroupTravel";
import cgConstants, { CorporateTravelDocs } from "../../config/cgConstants";
import { ICorporateGroupTravelContext } from "../../config/interfaces";
import CGQuoteApi from "../api/CGQuoteApi";
import BackArrowIcon from "../icons/BackArrowIcon";
import CGOrderSummary from "../page/CGOrderSummary";
import ProgressBar from "../page/ProgressBar";
import Spinner from "../page/Spinner";

const stripePromise = loadStripe(cgConstants.CGPublishableKey!);

const CGPaymentForm = () => {
  const { order } = useContext(CorporateGroupTravelContext) as ICorporateGroupTravelContext;
  const [loading, setLoading] = useState<boolean>(false);
  const [stripeIntentData, setStripeIntentData] = useState<any>(null);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const response: any = await CGQuoteApi.getPaymentIntent(order.order_number);
        if (response.errors) {
          throw response;
        }
        setStripeIntentData(response.data);
      } catch (e: any) {
        console.log(e.message);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [order?.order_number]);

  return (
    <>
      {loading && <Spinner />}
      <ProgressBar />
      <CGOrderSummary />
      <div className={appStyles.CGPaymentContainer}>
        {!loading && stripeIntentData && (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret: stripeIntentData.client_secret,
            }}
          >
            <PaymentFormElement />
          </Elements>
        )}
      </div>
    </>
  );
};

export default memo(CGPaymentForm);

const PaymentFormElement = () => {
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isStripeLoading, setIsStripeLoading] = useState(true);

  const { previous, setHasError, hasError, quoteResult, next } = useContext(
    CorporateGroupTravelContext
  ) as ICorporateGroupTravelContext;

  const stripe = useStripe();
  const elements: any = useElements();

  const initialValues = {
    isTermsAndConditionsChecked: false,
  };

  const validationSchema = yup.object().shape({
    isTermsAndConditionsChecked: yup.boolean().oneOf([true], "You must accept the terms and conditions"),
  });

  const handlePaymentSubmit = async (event: any) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    // event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsProcessingPayment(true);

    const { error } = await stripe.confirmPayment({
      //`Elements` instance that was used to create the Payment Element
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: "",
      },
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.

    if (error) {
      if (error.type === "card_error" || error.type === "validation_error") {
        setHasError(error.message || null);
      } else {
        setHasError("An unexpected error occurred.");
      }
      setIsProcessingPayment(false);
      return;
    } else {
      setIsProcessingPayment(false);
      next();
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handlePaymentSubmit}>
      {({ handleSubmit, values, handleBlur, errors, touched, validateForm, setFieldValue }) => (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            await validateForm();
            handleSubmit();
          }}
        >
          <div className={appStyles.borderBox}>
            <div className={appStyles.formWrapper}>
              <div className={`${appStyles.formRow} ${appStyles.paymentCardWrapper}`}>
                <PaymentElement
                  onReady={() => {
                    setIsStripeLoading(false);
                  }}
                />
              </div>
            </div>
          </div>

          <div className={appStyles.borderBox}>
            <div
              className={`${appStyles.formField} ${
                errors.isTermsAndConditionsChecked && touched.isTermsAndConditionsChecked ? appStyles.hasError : ""
              }`}
            >
              <div className={`${appStyles.bfCheckBox} ${appStyles.btCheckBox}`}>
                <label
                  htmlFor="isTermsAndConditionsChecked"
                  className={`${appStyles.formLabelHelper}
                  ${appStyles.btFormLabelHelper}`}
                >
                  <input
                    className={`${appStyles.btInputCheckBox}`}
                    type="checkbox"
                    id="isTermsAndConditionsChecked"
                    name="isTermsAndConditionsChecked"
                    checked={values?.isTermsAndConditionsChecked || false}
                    onChange={(event: any) => setFieldValue("isTermsAndConditionsChecked", event.target.checked)}
                    onBlur={handleBlur}
                  />
                  <span className={`${appStyles.bfCheckmark} ${appStyles.btCheckmark}`}></span>Checking here constitutes
                  my electronic signature. I confirm that I have read, understood and agreed to the&nbsp;
                  <a
                    className={appStyles.btLink}
                    href={CorporateTravelDocs?.termsAndConditions}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    plan document terms and conditions
                  </a>
                  , and&nbsp;
                  <a
                    className={appStyles.btLink}
                    href={CorporateTravelDocs?.noticeAndDisclosure}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    important notices and disclosures
                  </a>
                  , which includes fraud warnings, privacy notice, and consent to electronic signature and delivery.
                </label>
                <ErrorMessage name={"isTermsAndConditionsChecked"}>
                  {(msg) => <div className={appStyles.formError}>Please check the terms and conditions checkbox.</div>}
                </ErrorMessage>
              </div>
            </div>
          </div>

          {!isStripeLoading && (
            <div className={`${appStyles.paymentButtonWrapper} ${appStyles.formRow}`}>
              <div className={`${appStyles.buttonBlock} ${appStyles.buttonBlockNextPrev}`}>
                <button
                  className={`${appStyles.CGTBasicFormBackbtn}`}
                  type="button"
                  onClick={() => {
                    setHasError(null);
                    previous();
                  }}
                >
                  <BackArrowIcon />
                  Back
                </button>
                <button
                  className={`${appStyles.CGTBasicFormBtnPrimary}`}
                  type="submit"
                  disabled={isProcessingPayment || !stripe || !elements}
                >
                  {isProcessingPayment ? (
                    <span className={appStyles.SmallSpinner} />
                  ) : (
                    `Pay ${formatAmount(
                      quoteResult?.quoted_price,
                      quoteResult?.host_country,
                      quoteResult?.currency_id
                    )}`
                  )}
                </button>
              </div>
            </div>
          )}
          {hasError && (
            <div className={appStyles.formRow}>
              <div className={appStyles.battlefaceServerError}>{hasError}</div>
            </div>
          )}
        </form>
      )}
    </Formik>
  );
};
